import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  Box,
  Flex,
  Icon,
  Text,
  useColorMode,
  useColorModeValue,
  useDisclosure,
  theme,
} from "@chakra-ui/react";
import { useContext, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MdLock } from "react-icons/md";
import { AgencyContext } from "../Settings/AgencyProvider/AgencyProvider";
import { SettingContext } from "../Settings/Reducer/SettingProvider";
import FeatureUpgradeModal from "../shared/FeatureUpgradeModal";
import { clipText } from "../../utils/utils";

export function hasActiveChild(component, pathname) {
  if (pathname.includes(component.path)) return true;
  if (component.children) {
    return component.children.some((child) => hasActiveChild(child, pathname));
  }
  return false;
}

// True if the user's effective credits activate `feature_name`.
// `credits` shape mirrors backend get_effective_billing_credits.
function isFeatureActivated(credits, feature_name) {
  if (!feature_name) return true; // no gating
  const v = credits?.[feature_name];
  if (v === true) return true;
  if (typeof v === "number" && v >= 1) return true;
  if (typeof v === "string" && v.length > 0) return true;
  return false;
}

function RenderAccordion(component) {
  const isChild = component?.isChild;
  const hasChildren = component?.children?.length > 0;
  const location = useLocation();
  const isActive = location.pathname.includes(component.path);

  const agency = useContext(AgencyContext);
  const { state } = useContext(SettingContext) || { state: {} };
  const textColor = agency?.textColor;
  const iconColor = useColorModeValue(
    textColor || theme.colors.gray[500],
    "white"
  );

  const { colorMode } = useColorMode();
  const activeTitleColor =
    colorMode === "light"
      ? isActive
        ? textColor || "black"
        : "gray"
      : "white";

  // Feature gating: render the item but suppress navigation + show an
  // upgrade nudge when the user lacks the required feature.
  const locked =
    !!component?.requires_feature &&
    !isFeatureActivated(state?.credits, component.requires_feature);

  const {
    isOpen: upgradeOpen,
    onOpen: openUpgrade,
    onClose: closeUpgrade,
  } = useDisclosure();

  const handleLinkClick = (e) => {
    if (locked) {
      e.preventDefault();
      e.stopPropagation();
      openUpgrade();
    }
  };

  return (
    <AccordionItem
      data-tour={component?.id ? `sidebar.${component.id}` : undefined}
      bg={
        colorMode === "light"
          ? isActive
            ? "gray.200"
            : undefined
          : isActive
          ? "gray.600"
          : undefined
      }
      border={isChild && "none"}
      borderBottomWidth={"0px !important"}
    >
      <AccordionButton px={4} py={2} justifyContent={"space-between"}>
        <Link
          to={locked ? "#" : component.path}
          onClick={handleLinkClick}
          style={component?.path && { width: "100%", textAlign: "left" }}
        >
          <Flex alignItems={"center"} gap={3} opacity={locked ? 0.55 : 1}>
            {component?.icon && (
              <Icon as={component.icon} size="md" color={iconColor} />
            )}
            <Text fontWeight={"bold"} color={activeTitleColor}>
              {clipText(component.title, 20)}
            </Text>
            {locked && (
              <Box color="gray.400" ml={1}>
                <MdLock size={14} />
              </Box>
            )}
          </Flex>
        </Link>
        {hasChildren && <AccordionIcon />}
      </AccordionButton>

      {hasChildren && (
        <AccordionPanel pb={0} pt={0} pr={0}>
          <Accordion
            allowMultiple
            defaultIndex={[
              component.children.findIndex((child) =>
                hasActiveChild(child, location.pathname)
              ),
            ]}
          >
            {component.children.map((child, index) => (
              <RenderAccordion key={index} isChild={true} {...child} />
            ))}
          </Accordion>
        </AccordionPanel>
      )}

      {locked && (
        <FeatureUpgradeModal
          isOpen={upgradeOpen}
          onClose={closeUpgrade}
          title={component.title}
          description={component.upgrade_description}
          // White-label agencies have their own plan lineups we don't
          // enumerate here, so suppress the plan-badge row in that
          // case and let the modal render a generic upgrade CTA.
          availablePlans={
            agency?.myDetails?.agency_id ? [] : component.upgrade_plans
          }
        />
      )}
    </AccordionItem>
  );
}

const CustomAccordion = ({ navItem, forceOpen = false }) => {
  const location = useLocation();
  const shouldExpand = hasActiveChild(navItem, location.pathname);
  const defaultIndex = forceOpen || shouldExpand ? [0] : [];

  return (
    <Accordion allowMultiple defaultIndex={defaultIndex} w={"100%"}>
      <RenderAccordion {...navItem} />
    </Accordion>
  );
};

export default CustomAccordion;
